// PotMenu.tsx — ONE control where the fleet has two circles.
//
// 🔴 IDENTITY (2026-08-30): "wordmark-left + two-circular-controls-right" is one of the four
// banned fleet fingerprints. Muster lost its Boxes mark + "Muster" wordmark (the points-progress
// figure is the header now — the number is the identity), and the two round menu buttons collapse
// here into a single control drawn in this app's own material — a PAINT POT seen from above:
// square, with the same pigment depth the unit swatches carry (top highlight, shadowed foot).
// Deliberately not a third circle, and not any sibling's shape.
//
// The shared BackupMenu / AccountMenu are rendered UNCHANGED inside — the account-deletion flow
// the store gate depends on lives inside AccountMenu and must not be reimplemented here.

import { useState, type ReactNode } from "react";
import { platform } from "./tauri-web";
import { WalletTopUp } from "./iap";

export function PotMenu({ backup, account }: { backup: ReactNode; account: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Apple 2.1(b) (Focus/Gifted rejections, 2026-09-05): the wallet top-up must be visible
  // the moment this drawer opens, not nested one tap deeper inside AccountMenu's own avatar
  // popover. Rendered directly here, gated iOS/MAS same as AccountMenu's own gate.
  const isIos = platform() === "ios";
  const isMasMac = platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1";
  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="mst-potmenu" aria-label="Settings, backup and account" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 3.7v2.2M12 18.1v2.2M3.7 12h2.2M18.1 12h2.2M6.2 6.2l1.5 1.5M16.3 16.3l1.5 1.5M17.8 6.2l-1.5 1.5M7.7 16.3l-1.5 1.5"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="mst-menu-panel">
            {(isIos || isMasMac) && (
              <>
                <div style={{ padding: "0.5rem 0.5rem 0.15rem" }}><WalletTopUp /></div>
                <div className="mst-menu-rule" />
              </>
            )}
            <div className="mst-menu-row">{backup}<span>Back up &amp; restore</span></div>
            <div className="mst-menu-rule" />
            <div className="mst-menu-row">{account}<span>Account</span></div>
          </div>
        </>
      )}
    </div>
  );
}

// 🔴 THE ASSISTANT ENTRY — a paint pot docked to the army rail, replacing the floating "Chat"
// pill ~15 siblings shared. onAsk opens the same Chat sheet: moved, never removed.
export function AskPot({ onAsk }: { onAsk: () => void }) {
  return (
    <button type="button" className="mst-pot" onClick={onAsk} aria-label="Open chat assistant">
      <span className="mst-pot-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none">
          <path d="M6.2 8.4h11.6v9.4a2.4 2.4 0 0 1-2.4 2.4H8.6a2.4 2.4 0 0 1-2.4-2.4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M4.9 5.1h14.2v3.3H4.9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M9.4 13.2h5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </span>
      <span className="mst-pot-txt">
        <span className="mst-pot-lbl">Ask</span>
        <span className="mst-pot-sub">log · pipeline · recap</span>
      </span>
    </button>
  );
}
